import React, { useState, useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router';

import {
  AutoComplete,
  Button,
  ConfirmModal,
  Copy,
  DynamicTopPanel,
  Input,
  JsonFormBuilder,
  Loading,
} from '..';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { usePermissionAllowed } from '../../hooks';
import type { FormJsonData, AgentWidgetSettings } from '../../api';

type FormBuilderModalProps = {
  onChange: (data: any) => void;
  widgetSettings: AgentWidgetSettings | null;
  schemaData: FormJsonData;
  intializing: boolean;
  onSave?: () => void;
  showSaveButton?: boolean;
  handleFileUpload?: (file: Blob) => Promise<string | undefined>;
  options?: any;
  sourceAgentOptions?: OptionType[];
  currentAgentName?: string;
  onApplyFromAgent?: (agentId: string) => void | Promise<void>;
  applyFromAgentLoading?: boolean;
};

export const WidgetSettingsBuilder: React.FC<FormBuilderModalProps> = ({
  widgetSettings,
  onChange,
  schemaData,
  intializing,
  onSave,
  showSaveButton = false,
  handleFileUpload,
  options,
  sourceAgentOptions,
  currentAgentName,
  onApplyFromAgent,
  applyFromAgentLoading = false,
}) => {
  const { params } = useRouteMatch<{ organization_id: string }>();
  const isAllowedAgentEdit = usePermissionAllowed(PERMISSIONS.EDIT_AGENTS);

  const [isError, setIsError] = useState(false);
  const [selectedSourceAgentId, setSelectedSourceAgentId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);

  const showImportRow =
    !!sourceAgentOptions?.length && !!onApplyFromAgent && isAllowedAgentEdit;

  const selectedAgentLabel = useMemo(
    () =>
      sourceAgentOptions?.find(o => o.value === selectedSourceAgentId)?.label ??
      '',
    [sourceAgentOptions, selectedSourceAgentId]
  );

  const handleChanges = useCallback(
    ({ data, errors }: { data: any; errors: any }) => {
      const isLocal = process.env.NODE_ENV === 'development';
      const isDev =
        process.env.REACT_APP_ENV === 'dev' ||
        (typeof window !== 'undefined' &&
          window.location.hostname.includes('dev-admin'));

      const shouldLog = isLocal || isDev;

      if (shouldLog) {
        console.log('WidgetSettingsBuilder - data:', data);
        console.log('WidgetSettingsBuilder - errors:', errors);
      }

      onChange(data);
      setIsError(!!errors.length);
    },
    [onChange]
  );

  const handleApplyClick = useCallback(() => {
    if (!selectedSourceAgentId) return;
    setShowConfirm(true);
  }, [selectedSourceAgentId]);

  const handleConfirmApply = useCallback(async () => {
    setShowConfirm(false);
    if (!selectedSourceAgentId || !onApplyFromAgent) return;
    await onApplyFromAgent(selectedSourceAgentId);
    setSelectedSourceAgentId('');
  }, [selectedSourceAgentId, onApplyFromAgent]);

  const widgetSettingsJson = useMemo(
    () => JSON.stringify(widgetSettings, null, 2) || '',
    [widgetSettings]
  );

  return (
    <>
      {intializing && !widgetSettings ? (
        <Loading />
      ) : (
        <div>
          <div className="px-4">
            <div className="flex justify-end gap-x-md mb-2">
              <div className="text-small truncate">Copy Widget Settings</div>
              <Copy className="shrink-0" text={widgetSettingsJson} />
            </div>
            <Input value={widgetSettingsJson} multiline readonly />

            {showImportRow && (
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-x-md mt-4">
                <div className="text-small shrink-0 whitespace-nowrap">
                  Copy settings from agent
                </div>
                <div className="flex items-center gap-x-md flex-1 min-w-0">
                  <div className="flex-1 min-w-0">
                    <AutoComplete
                      options={sourceAgentOptions!}
                      value={selectedSourceAgentId}
                      onChange={setSelectedSourceAgentId}
                      placeholder="Select agent…"
                      disabled={applyFromAgentLoading || intializing}
                    />
                  </div>
                  <Button
                    label="Apply"
                    size="medium"
                    type="primary"
                    className="shrink-0"
                    disabled={
                      !selectedSourceAgentId ||
                      applyFromAgentLoading ||
                      intializing
                    }
                    loading={applyFromAgentLoading}
                    onClick={handleApplyClick}
                  />
                </div>
              </div>
            )}
          </div>
          <JsonFormBuilder
            onChange={handleChanges}
            formData={widgetSettings || {}}
            schemaData={schemaData}
            onChangeUpload={handleFileUpload}
            options={options}
          />
        </div>
      )}

      {showImportRow && (
        <ConfirmModal
          open={showConfirm}
          title=""
          saving={applyFromAgentLoading}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirmApply}
          confirmButtonLabel="Confirm"
          confirmButtonClassName="w-full bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600"
        >
          <p className="flex flex-wrap justify-center gap-x-1 text-mainText text-small">
            <span>Are you sure you want to copy settings from</span>
            <span className="font-medium">{selectedAgentLabel}</span>
            <span>to</span>
            <span className="font-medium">
              {currentAgentName ?? 'Current agent'}?
            </span>
            <span className="font-medium text-center text-small mt-md">
              This updates the editor state only — changes are saved when you
              click Save.
            </span>
          </p>
        </ConfirmModal>
      )}

      {showSaveButton && isAllowedAgentEdit && (
        <DynamicTopPanel>
          <div className="flex flex-row gap-x-md justify-center items-center">
            <Button
              label="Cancel"
              url={`/agents/${params.organization_id}`}
              type="contrast"
              size="medium"
            />
            <Button
              label={'Save Widget Settings'}
              size="medium"
              type="primary"
              disabled={intializing || isError}
              onClick={onSave}
            />
          </div>
        </DynamicTopPanel>
      )}
    </>
  );
};
